// shared reactive state for the horizon nav chrome (FI-1869). the top bar
// and the rail are separate components; this keeps collapse + mobile-drawer
// state in one observable so neither owns the other.
import Vue from 'vue'

const COLLAPSE_KEY = 'fi_nav_collapsed'

// below this width the rail always collapses (on load and whenever a resize
// crosses under it); the user can re-expand manually at any time, and that
// expansion holds until the next crossing. above it, the saved choice wins.
export const NAV_COLLAPSE_WIDTH = 1600
export const NAV_COLLAPSE_QUERY = `(max-width: ${NAV_COLLAPSE_WIDTH - 1}px)`

function initialCollapsed() {
  if (typeof window !== 'undefined' && window.innerWidth < NAV_COLLAPSE_WIDTH) return true
  try {
    const saved = localStorage.getItem(COLLAPSE_KEY)
    if (saved === '1') return true
    if (saved === '0') return false
  } catch (_) { /* storage unavailable */ }
  return false
}

// v2: the map went tri-state (true = user-opened, false = user-collapsed,
// absent = follow the active-section rule), so the old key's accumulated
// auto-expansions must not carry over as explicit opens
const EXPAND_KEY = 'fi_nav_expanded2'

function initialExpanded() {
  try {
    const saved = JSON.parse(localStorage.getItem(EXPAND_KEY) || '{}')
    if (saved && typeof saved === 'object') return saved
  } catch (_) { /* storage unavailable or corrupt */ }
  return {}
}

// per-group folder state, keyed '<sectionKey>:<groupKey>'. groups default
// OPEN (the tree should read whole on arrival), so only an explicit user
// collapse is ever stored -- absence means open.
const GROUP_KEY = 'fi_nav_groups'

function initialGroups() {
  try {
    const saved = JSON.parse(localStorage.getItem(GROUP_KEY) || '{}')
    if (saved && typeof saved === 'object') return saved
  } catch (_) { /* storage unavailable or corrupt */ }
  return {}
}

// the rail hides itself at max-width:991px (fi_section_nav @media; App.vue
// drops the content margin at the same width) and the bar switches to its
// mobile arrangement on the same line. ONE definition, because the bar and
// the rail now have to agree on it rather than merely look alike: the tenant
// switcher renders in exactly one of them, never both.
//
// matchMedia rather than innerWidth: innerWidth counts the scrollbar, so with
// always-on scrollbars a ~15px band (~992-1006px innerWidth) read as desktop
// while the css had already hidden the rail.
export const RAIL_HIDDEN_QUERY = '(max-width: 991px)'

export function railHiddenNow() {
  try {
    return !!(window.matchMedia && window.matchMedia(RAIL_HIDDEN_QUERY).matches)
  } catch (_) {
    return false
  }
}

export const navState = Vue.observable({
  collapsed: initialCollapsed(),
  mobileOpen: false,
  // kept live by the listener below; components read it instead of running
  // their own matchMedia, so they cannot drift apart
  railHidden: railHiddenNow(),
  // per-section tree expansion overrides. keys are present only where the
  // user toggled: true sticks a section open across navigation, false holds
  // it closed; a section with NO key renders open exactly while it is the
  // active section (the rail decides that -- see fi_section_nav.isExpanded)
  expanded: initialExpanded(),
  // per-group folder overrides (see GROUP_KEY above)
  groups: initialGroups(),
})

// one module-level listener keeps navState.railHidden honest for every
// consumer. safe at import time: guarded for environments with no matchMedia
// (unit tests), and there is exactly one navState.
try {
  if (typeof window !== 'undefined' && window.matchMedia) {
    const mq = window.matchMedia(RAIL_HIDDEN_QUERY)
    const onChange = e => { navState.railHidden = e.matches }
    // addListener is the safari < 14 spelling
    if (mq.addEventListener) mq.addEventListener('change', onChange)
    else if (mq.addListener) mq.addListener(onChange)

    // resizing under the collapse width always collapses the rail. this only
    // sets live state (no localStorage write): it is an automatic action, not
    // a user choice, so a manual re-expand afterwards still works and the
    // saved preference still governs wide viewports.
    const collapseMq = window.matchMedia(NAV_COLLAPSE_QUERY)
    const onCollapseChange = e => { if (e.matches) navState.collapsed = true }
    if (collapseMq.addEventListener) collapseMq.addEventListener('change', onCollapseChange)
    else if (collapseMq.addListener) collapseMq.addListener(onCollapseChange)
  }
} catch (_) { /* no matchMedia: railHidden stays at its initial value */ }

// touch-primary devices (phones/tablets report hover:none regardless of
// viewport size) emulate a mouseenter on tap, which pops the chrome's hover
// tooltips and leaves them stuck open over the popovers -- tooltip builders
// return an empty config when this is true. evaluated per call so tests can
// stub matchMedia.
export function noHoverDevice() {
  try {
    return !!(window.matchMedia && window.matchMedia('(hover: none)').matches)
  } catch (_) {
    return false
  }
}

export function setCollapsed(value) {
  navState.collapsed = !!value
  try { localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0') } catch (_) { /* ignore */ }
}

export function toggleCollapsed() {
  setCollapsed(!navState.collapsed)
}

export function setMobileOpen(value) {
  navState.mobileOpen = !!value
}

export function setSectionExpanded(key, value) {
  // replace the object so vue 2 reactivity picks up new keys
  navState.expanded = { ...navState.expanded, [key]: !!value }
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify(navState.expanded)) } catch (_) { /* ignore */ }
}

// drop a section's override so it falls back to the active-section rule
// (used when the user arrives in a section they had explicitly collapsed)
export function clearSectionExpanded(key) {
  const next = { ...navState.expanded }
  delete next[key]
  navState.expanded = next
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify(navState.expanded)) } catch (_) { /* ignore */ }
}

export function setGroupOpen(key, value) {
  navState.groups = { ...navState.groups, [key]: !!value }
  try { localStorage.setItem(GROUP_KEY, JSON.stringify(navState.groups)) } catch (_) { /* ignore */ }
}

// drop a folder's override so it returns to the open default. used when the
// user navigates INTO a folder they had collapsed -- otherwise the row for
// the page they are on would not render at all.
export function clearGroupOpen(key) {
  if (!(key in navState.groups)) return
  const next = { ...navState.groups }
  delete next[key]
  navState.groups = next
  try { localStorage.setItem(GROUP_KEY, JSON.stringify(navState.groups)) } catch (_) { /* ignore */ }
}
